import {BehaviorSubject, Observable, of, Subscription, throwError} from 'rxjs';
import {Injectable, OnDestroy} from '@angular/core';
import {BasepermissionService, PermissionResponse} from "../base-api";
import {catchError, concatMap, delay, flatMap, map, retryWhen, tap} from 'rxjs/operators';
import PermissionsEnum = PermissionResponse.PermissionsEnum;


@Injectable()
export class AppPermissionService implements OnDestroy {

  private initialized = false;
  private permissionSubject: BehaviorSubject<PermissionsEnum[]> = new BehaviorSubject<PermissionsEnum[]>([]);
  private _permissions$: Observable<PermissionsEnum[]> = this.permissionSubject.asObservable();

  private initializedSubject = new BehaviorSubject<boolean>(false);
  private _initialized$: Observable<boolean> = this.initializedSubject.asObservable();

  private subscription: Subscription;

  private NUMBER_OF_RETRIES = 2;

  constructor(private permissionService: BasepermissionService) {
  }

  get permissions$(): Observable<PermissionResponse.PermissionsEnum[]> {
    if (!this.initialized) {
      return this.permissionService.getAllPermissions().pipe(
        flatMap(data => {
          if (data.permissions.length === 0) {
            return throwError('retry');
          } else {
            this.initializedSubject.next(true);
            this.initialized = true;
            return of(data);
          }
        }),
        retryWhen(errors => {
          return errors.pipe(
            concatMap((e, i) => {
              if (i >= this.NUMBER_OF_RETRIES || e !== 'retry') {
                return throwError(e);
              }
              return of(e);
            }), delay(500));
        }),
        catchError(err => {
          if (err === "retry") {
            return of({permissions: []});
          } else {
            throw err;
          }
        }),
        tap(p => this.permissionSubject.next(p.permissions)),
        tap(p => {
          if (p.permissions.length !== 0) {
            this.initializedSubject.next(true);
            this.initialized = true;
          }
        }),
        map(p => p.permissions)
      );
    }
    return this._permissions$;
  }

  refreshPermissions() {
    this.subscription = this.permissionService.getAllPermissions().subscribe(data => this.permissionSubject.next(data.permissions));
  }

  hasPermission(permission: PermissionResponse.PermissionsEnum): boolean {
    return this.permissionSubject.getValue().includes(permission);
  }

  hasAtLeastOnePermission(permissions: Array<PermissionResponse.PermissionsEnum>): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  hasAllPermissions(permissions: Array<PermissionResponse.PermissionsEnum>): boolean {
    return permissions.every(p => this.hasPermission(p));
  }

  get initialized$(): Observable<boolean> {
    return this._initialized$;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }


}
